import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Globe, Palette, Shield } from 'lucide-react';
import { CompetePagesIcon } from '../components/CompetePagesIcon';

export const Welcome: React.FC = () => {
  return (
    <div className="page-container">
      {/* Header */}
      <header className="page-header sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img
                src="/branding/competepages-logo.png"
                alt="CompetePages"
                className="h-10 w-auto object-contain"
              />
              <img
                src="/roary/roary-full.png"
                title="Roary the Spokes-Dragon"
                alt="Roary mascot"
                className="h-14 w-auto object-contain"
              />
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="btn-ghost">
                Sign In
              </Link>
              <Link to="/signup" className="btn-primary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <div className="inline-flex items-center justify-center text-primary-700 text-lg font-medium mb-8">
              Convenience meets Beauty and Performance
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight mb-6">
              Build Beautiful Websites
              <span className="block bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent pb-2">
                Without Coding
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed">
              Create stunning, professional websites with a simple intuitive editor. No technical
              skills required. Launch your site in minutes, not days.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/signup" className="btn-primary text-lg px-8 py-4 group">
                Start Building Free
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" className="btn-secondary text-lg px-8 py-4">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything you need to build amazing websites
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features designed to help you create professional websites quickly and easily
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {/* Feature 1 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Drag & Drop Editor</h3>
              <p className="text-gray-600 leading-relaxed">
                Build your website visually with our intuitive drag-and-drop interface. No coding
                knowledge required.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized for performance with fast loading times and smooth interactions on all
                devices.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Responsive Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Your websites will look great on all devices - desktop, tablet, and mobile
                automatically.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure & Reliable</h3>
              <p className="text-gray-600 leading-relaxed">
                Built with security in mind. Your data is safe and your websites are always
                available.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-pink-500 to-pink-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <CompetePagesIcon size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Professional Templates</h3>
              <p className="text-gray-600 leading-relaxed">
                Start with beautiful, professionally designed templates and customize them to match
                your brand.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">One-Click Publish</h3>
              <p className="text-gray-600 leading-relaxed">
                Publish your website with a single click. No complex deployment processes or
                technical setup required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to build your first site?</h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Join other creators who have built amazing websites with our platform. Start your free
            trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-primary text-lg px-8 py-4 group">
              Start Building Now
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/login" className="btn-secondary text-lg px-8 py-4">
              Sign In to Continue
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/branding/competepages-logo.png"
                alt="CompetePages"
                className="h-8 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-gray-400">
              Create beautiful, professional websites without any coding knowledge. Built for
              creators, entrepreneurs, and businesses.
            </p>
            <div className="mt-8 pt-8 border-t border-gray-800 text-sm text-gray-500">
              © {new Date().getFullYear()} Compete Pages by Compete Services LLC - All rights
              reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
